import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  ArrowRight, CheckCircle2,
  Link2, ArrowDown, Zap, FileText, Building2, Globe,
  RefreshCw, Clock, Shield, Users, Hotel
} from 'lucide-react';

/* ─── scroll-reveal ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-el ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function SectionTag({ children }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-[#2563EB] text-sm font-medium mb-4">
      {children}
    </span>
  );
}

/* ─── Integration Card ─── */
function IntegrationCard({ name, logo, status, subtext, isLive, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className={`bg-white rounded-2xl border p-6 hover:shadow-lg transition-all group ${isLive ? 'border-green-300 ring-2 ring-green-100' : 'border-slate-200 hover:border-[#2563EB]/30'}`} data-testid={`integration-card-${name.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex flex-col items-center text-center">
          {/* Logo placeholder - using first letter as fallback */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform overflow-hidden">
            {logo ? (
              <img src={logo} alt={name} className="w-14 h-14 object-contain" />
            ) : (
              <span className="text-3xl font-bold text-slate-400">{name.charAt(0)}</span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">{name}</h3>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
            isLive 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : status.includes('Launching') 
                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
          }`}>
            {!isLive && !status.includes('Launching') && <Clock className="w-3 h-3" />}
            {status}
          </span>
          {subtext && (
            <p className={`text-xs mt-2 ${isLive ? 'text-green-600' : 'text-slate-500'}`}>{subtext}</p>
          )}
        </div>
      </div>
    </Reveal>
  );
}

/* ─── Benefit Card ─── */
function BenefitCard({ icon: Icon, title, description, delay = 0 }) {
  return (
    <Reveal delay={delay}>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-[#2563EB]/30 hover:shadow-lg transition-all" data-testid={`benefit-${title.slice(0, 20).toLowerCase().replace(/\s/g, '-')}`}>
        <div className="w-12 h-12 rounded-xl bg-[#2563EB]/10 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#2563EB]" />
        </div>
        <h3 className="font-semibold text-slate-900 mb-2">{title}</h3>
        <p className="text-slate-600 text-sm leading-relaxed">{description}</p>
      </div>
    </Reveal>
  );
}

/* ═══════════════════════ INTEGRATIONS PAGE ═══════════════════════ */
import beds24Logo from '../../assets/images/partners/beds24.jpeg';
import littleHotelierLogo from '../../assets/images/partners/little-hotelier.jpeg';
import guestyLogo from '../../assets/images/partners/guesty.jpeg';

const Integrations = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // PMS Integrations
  const pmsIntegrations = [
    { name: 'Beds24', logo: beds24Logo, status: 'Officially Live – 23 October 2026', subtext: 'Now available for Malaysian operators' },
    { name: 'Little Hotelier', logo: littleHotelierLogo, status: 'Integration in progress' },
    { name: 'Guesty', logo: guestyLogo, status: 'Integration in progress' },
    { name: 'Mews', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/884kvqos_IMG_3496.png', status: 'Launching July 2026', subtext: 'Join waitlist for priority access' },
    { name: 'Hostaway', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/i683ll0w_IMG_3497.png', status: 'Integration in progress' },
    { name: 'Lodgify', logo: 'https://customer-assets.emergentagent.com/job_af234a36-b3fc-42c2-a986-f11930d0eee9/artifacts/lmye8r4y_IMG_3493.png', status: 'Integration in progress' },
  ];

  // Channel Manager Integrations
  const channelManagerIntegrations = [];

  // Integration Benefits
  const benefits = [
    {
      icon: FileText,
      title: 'Automatic e-Invoice Generation',
      description: 'Automatically generate Malaysia LHDN compliant e-Invoices from your booking transactions without manual data entry.'
    },
    {
      icon: Link2,
      title: 'Seamless System Integration',
      description: 'Connect your existing hospitality management systems with StayMii through secure API integrations.'
    },
    {
      icon: Shield,
      title: 'Automated Tax Compliance',
      description: 'Stay compliant with Malaysian tax regulations through automated MyInvois submissions and validations.'
    },
    {
      icon: Zap,
      title: 'Reduced Manual Work',
      description: 'Eliminate manual accounting and administrative tasks with end-to-end automation from booking to invoice.'
    },
  ];

  return (
    <>
      <SEO
        title="Integrations — PMS e-Invoice Integration for Beds24 & Channel Managers"
        description="StayMii integrates with Beds24, Little Hotelier, Guesty, Mews & more to automate Malaysia LHDN e-invoice generation from your hotel PMS and channel manager bookings."
        keywords="pms e invoice integration, channel manager e invoice, beds24 integration malaysia, hotel pms integration, e invoice plugin malaysia, myinvois api integration"
        canonicalUrl="/integrations"
      />

      <style>{`
        .reveal-el { opacity:0; transform:translateY(24px); transition: opacity .6s cubic-bezier(.16,1,.3,1), transform .6s cubic-bezier(.16,1,.3,1); }
        .reveal-el.revealed { opacity:1; transform:translateY(0); }
        .flow-arrow { animation: flowPulse 2s ease-in-out infinite; }
        @keyframes flowPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
      `}</style>

      <div className="min-h-screen bg-white" data-testid="integrations-page">

        {/* ═══════ NAV ═══════ */}
        <LandingNavBar />

        {/* ═══════ HERO ═══════ */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="integrations-hero">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <SectionTag><Link2 className="h-4 w-4" /> Integrations</SectionTag>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                PMS e-Invoice Integration
                <span className="block text-[#60A5FA] mt-2">for Hotels & Channel Managers</span>
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                Connect your hotel PMS or channel manager to StayMii and automatically generate LHDN-compliant e-invoices from your booking transactions via the MyInvois system. Beds24, Little Hotelier, Guesty & more.
              </p>
            </Reveal>
          </div>
        </section>

        {/* ═══════ PMS INTEGRATIONS ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="pms-integrations-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Building2 className="h-4 w-4" /> PMS & Channel Managers</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Hotel PMS e-Invoice Integrations</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Connect your PMS or channel manager to StayMii for seamless e-invoice automation.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {pmsIntegrations.map((integration, i) => (
                <IntegrationCard key={integration.name} {...integration} delay={i * 60} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ CHANNEL MANAGER INTEGRATIONS ═══════ */}
        {channelManagerIntegrations.length > 0 && (
        <section className="py-16 lg:py-24 bg-white" data-testid="channel-manager-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><Globe className="h-4 w-4" /> Channel Managers</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Channel Manager e-Invoice Integration</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Integrate your channel manager to automatically sync bookings and generate e-invoices.
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 max-w-2xl mx-auto">
              {channelManagerIntegrations.map((integration, i) => (
                <IntegrationCard key={integration.name} {...integration} delay={i * 60} />
              ))}
            </div>
          </div>
        </section>
        )}

        {/* ═══════ INTEGRATION WORKFLOW ═══════ */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]" data-testid="workflow-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><RefreshCw className="h-4 w-4" /> How It Works</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">How PMS e-Invoice Integration Works</h2>
                <p className="text-lg text-white/70 max-w-2xl mx-auto">
                  See how StayMii connects your booking systems to Malaysia's e-invoice infrastructure.
                </p>
              </div>
            </Reveal>

            {/* Flow Diagram */}
            <Reveal delay={100}>
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12">
                <div className="flex flex-col items-center space-y-6">
                  
                  {/* Step 1: OTA Platforms */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#FF5A5F]/10 flex items-center justify-center">
                          <Globe className="w-6 h-6 text-[#FF5A5F]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">OTA Platforms</h3>
                          <p className="text-sm text-slate-500">Online Travel Agencies</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#FF5A5F]/10 text-[#FF5A5F]">Airbnb</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#003580]/10 text-[#003580]">Booking.com</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#5392F9]/10 text-[#5392F9]">Agoda</span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 2: PMS / Channel Manager */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">PMS / Channel Manager</h3>
                          <p className="text-sm text-slate-500">PMS & Channel Managers</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">Beds24</span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">+ More</span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 3: StayMii */}
                  <div className="w-full max-w-md">
                    <div className="bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-2xl p-6 shadow-xl">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <Hotel className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">StayMii</h3>
                          <p className="text-sm text-white/70">e-Invoice Automation Platform</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex flex-col items-center flow-arrow">
                    <ArrowDown className="w-8 h-8 text-[#2563EB]" />
                  </div>

                  {/* Step 4: LHDN MyInvois */}
                  <div className="w-full max-w-md">
                    <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-emerald-200">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">LHDN MyInvois System</h3>
                          <p className="text-sm text-slate-500">Malaysia e-Invoice Infrastructure</p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 ml-auto" />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ INTEGRATION BENEFITS ═══════ */}
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="benefits-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <SectionTag><CheckCircle2 className="h-4 w-4" /> Benefits</SectionTag>
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Why Hotels Choose StayMii for e-Invoice Integration</h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Why hotels choose StayMii for their e-invoice automation needs.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, i) => (
                <BenefitCard key={benefit.title} {...benefit} delay={i * 80} />
              ))}
            </div>
          </div>
        </section>

        {/* ═══════ RELATED e-INVOICE PAGES ═══════ */}
        <section className="py-16 bg-white border-t border-slate-100" data-testid="related-pages-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Explore e-Invoice Malaysia Solutions</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { to: '/e-invoice-malaysia', label: 'e-Invoice Malaysia Guide', desc: 'Complete guide to LHDN digital invoicing' },
                  { to: '/lhdn-e-invoice', label: 'LHDN e-Invoice Compliance', desc: 'Requirements & deadlines' },
                  { to: '/myinvois-api', label: 'MyInvois API Integration', desc: 'Automate e-invoice submission' },
                  { to: '/hotel-e-invoice-system', label: 'Hotel e-Invoice System', desc: 'Purpose-built for hotels' },
                  { to: '/pms-e-invoice-integration', label: 'PMS e-Invoice Integration', desc: 'Connect your PMS to LHDN' },
                  { to: '/airbnb-e-invoice-malaysia', label: 'Airbnb e-Invoice', desc: 'For short-term rental hosts' },
                ].map((link, i) => (
                  <Link key={i} to={link.to} className="block p-4 bg-slate-50 hover:bg-blue-50 rounded-xl border border-slate-200 hover:border-blue-200 transition-all group">
                    <p className="font-semibold text-slate-900 group-hover:text-[#2563EB] text-sm mb-1">{link.label}</p>
                    <p className="text-xs text-slate-500">{link.desc}</p>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ CTA ═══════ */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-[#2563EB] via-[#1D4ED8] to-[#1E40AF]" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">Partner With StayMii</h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                Interested in integrating your platform with StayMii? We're expanding our integration ecosystem and would love to hear from you.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="group px-8 py-4 bg-white text-[#2563EB] rounded-xl font-semibold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2" data-testid="cta-request-btn">
                  Request Integration <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/einvoice/portal/auth" className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold text-lg hover:bg-white/20 transition-all border border-white/30 flex items-center justify-center gap-2" data-testid="cta-trial-btn">
                  <Users className="h-5 w-5" /> Start Free Trial
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════ FOOTER ═══════ */}
        <LandingFooter />
      </div>
    </>
  );
};

export default Integrations;
